/**
 * Boli Live - Auction Room Page
 *
 * eBay-style Live Auction Room.
 * Video background + Bottom Sheet for controls.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Users, X, Wifi, WifiOff } from 'lucide-react';
import { useAuction, useBids, usePlaceBid, useCountdown, useSuggestedBids } from '@boli/core';
import { BidSlider } from '../components/BidSlider';
import { IVSVideoPlayer } from '../components/IVSVideoPlayer';
import { useAuthStore } from '../lib/stores/auth-store';
import { formatPKR, cn } from '../lib/utils';

import { BidHistoryModal } from '../components/BidHistoryModal';
import { SellerInfoCard } from '../components/SellerInfoCard';
import { API_BASE_URL } from '../lib/api-config';
import type { Bid } from '@boli/types';

interface StreamInfo {
  playbackUrl?: string;
  isLive: boolean;
  viewerCount: number;
}

export default function AuctionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const bidsContainerRef = useRef<HTMLDivElement>(null);

  const { auction, isLoading: auctionLoading, isRealtimeConnected } = useAuction(id || '');
  const { bids } = useBids(id || '');
  const { placeBid, isPlacing } = usePlaceBid(id || '');

  const currentBid = auction?.currentHighBid || auction?.startingPrice || 0;
  const { formatted, isEnded, isEndingSoon } = useCountdown(auction?.endTimestamp || 0);
  const { suggestedAmounts } = useSuggestedBids(currentBid);

  const [selectedBidAmount, setSelectedBidAmount] = useState(suggestedAmounts[0] || 0);
  const [isBidHistoryOpen, setIsBidHistoryOpen] = useState(false);
  const [streamInfo, setStreamInfo] = useState<StreamInfo>({
    isLive: false,
    viewerCount: 0,
  });

  // Fetch stream info
  useEffect(() => {
    if (!id) return;

    const fetchStreamInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/streams/${id}`);
        if (response.ok) {
          const data = await response.json();
          setStreamInfo({
            playbackUrl: data.playbackUrl,
            isLive: data.isLive || data.status === 'live',
            viewerCount: data.viewerCount || 0,
          });
        }
      } catch {
        // Use auction streamUrl as fallback
        if (auction?.streamUrl) {
          setStreamInfo({
            playbackUrl: auction.streamUrl,
            isLive: auction.status === 'live',
            viewerCount: auction.viewerCount || 0,
          });
        }
      }
    };

    fetchStreamInfo();
  }, [id, auction]);

  // Report viewer joined/left
  const handleViewerJoined = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/streams/${id}/viewer-joined`, {
        method: 'POST',
      });
    } catch {
      // Silent fail
    }
  };

  const handleViewerLeft = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/streams/${id}/viewer-left`, {
        method: 'POST',
      });
    } catch {
      // Silent fail
    }
  };

  useEffect(() => {
    if (suggestedAmounts[0]) {
      setSelectedBidAmount(suggestedAmounts[0]);
    }
  }, [suggestedAmounts]);

  useEffect(() => {
    if (bidsContainerRef.current) {
      bidsContainerRef.current.scrollTop = bidsContainerRef.current.scrollHeight;
    }
  }, [bids]);

  const handleBid = async () => {
    if (!isAuthenticated) {
      navigate('/profile', { state: { from: `/auction/${id}` } });
      return;
    }

    await placeBid(selectedBidAmount, user?.id || '');
  };

  if (auctionLoading || !auction) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-12 w-12 bg-muted rounded-full mb-4" />
          <div className="h-4 w-32 bg-muted rounded" />
        </div>
      </div>
    );
  }

  const isLive = auction.status === 'live' || auction.status === 'extended';

  return (
    <div className="fixed inset-0 bg-black flex flex-col z-50">
      {/* Video/Image Background */}
      <div className="absolute inset-0 z-0">
        <IVSVideoPlayer
          streamUrl={streamInfo.playbackUrl}
          fallbackImage={auction.images?.[0] || '/placeholder.jpg'}
          isLive={streamInfo.isLive || isLive}
          autoplay={true}
          muted={true}
          showControls={false}
          onViewerJoined={handleViewerJoined}
          onViewerLeft={handleViewerLeft}
          className="w-full h-full"
        />
        {/* Simple gradient for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80 pointer-events-none" />
      </div>

      {/* Top Controls */}
      <div className="relative z-10 p-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full bg-black/20 text-white backdrop-blur-md hover:bg-black/40 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="flex items-center gap-3">
          {isLive && (
            <div className="bg-red-600 px-2 py-1 rounded text-white text-xs font-bold uppercase tracking-wide flex items-center gap-1">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              LIVE
            </div>
          )}
          <button className="p-2 rounded-full bg-black/20 text-white backdrop-blur-md hover:bg-black/40 transition-colors">
            <Share2 className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Middle Area (Bids Overlay) */}
      <div className="flex-1 relative z-10 pointer-events-none">
        {/* Connection Status & Viewer Count Floating */}
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          {/* Realtime Connection Status */}
          <div
            className={cn(
              "bg-black/40 backdrop-blur text-white text-xs px-2 py-1 rounded-md flex items-center gap-1",
              isRealtimeConnected ? "text-green-400" : "text-yellow-400"
            )}
            title={isRealtimeConnected ? "Live updates active" : "Using polling (5s refresh)"}
          >
            {isRealtimeConnected ? (
              <>
                <Wifi className="w-3 h-3" />
                <span>Live</span>
              </>
            ) : (
              <>
                <WifiOff className="w-3 h-3" />
                <span>Polling</span>
              </>
            )}
          </div>

          {/* Viewer Count */}
          {(isLive || streamInfo.isLive) && (
            <div className="bg-black/40 backdrop-blur text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
              <Users className="w-3 h-3" />
              {streamInfo.viewerCount || auction.viewerCount || 0}
            </div>
          )}
        </div>

        {/* Live Bids Feed (Bottom Left) */}
        <div className="absolute bottom-4 left-4 w-2/3 h-48 pointer-events-auto">
          <div
            ref={bidsContainerRef}
            className="h-full overflow-y-auto no-scrollbar flex flex-col justify-end space-y-2 mask-gradient-top"
          >
            <AnimatePresence initial={false}>
              {bids.slice(-10).map((bid: Bid) => (
                <motion.div
                  key={bid.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-start gap-2 text-white text-sm"
                >
                  <div className="font-bold text-white/90">{bid.bidderId.slice(0, 8)}:</div>
                  <div className="font-medium text-yellow-400 font-display">{formatPKR(bid.amount)}</div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Bottom Sheet (Controls) */}
      <div className="relative z-20 bg-background rounded-t-3xl p-5 pb-8 shadow-2xl safe-bottom animate-slide-up max-h-[60vh] overflow-y-auto no-scrollbar">
        {/* Drag Handle (Visual only) */}
        <div className="w-12 h-1.5 bg-muted rounded-full mx-auto mb-4 sticky top-0" />

        {/* Product Info */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex-1 pr-4">
            <h1 className="text-lg font-bold text-foreground line-clamp-2">{auction.title}</h1>
            <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
              <button
                onClick={() => setIsBidHistoryOpen(true)}
                className="font-medium text-primary hover:underline hover:text-primary/80 transition-colors"
              >
                {auction.bidCount} bids
              </button>
              <span>•</span>
              <span className={isEndingSoon ? "text-red-500 font-bold" : ""}>
                {isEnded ? "Ended" : `${formatted} left`}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground uppercase">Current Bid</div>
            <div className="text-2xl font-bold text-primary font-display">{formatPKR(currentBid)}</div>
          </div>
        </div>

        {/* Seller Info (New) */}
        <div className="mb-6">
          <SellerInfoCard
            sellerId={auction.sellerId}
            sellerName="TopRatedSeller" // Mock data for now
            rating={4.9}
            feedbackCount={154}
            positivePercentage={99.5}
            location="Karachi, PK"
          />
        </div>

        {/* Bidding Controls */}
        {isLive && !isEnded ? (
          <div className="space-y-4">
            {/* Quick Suggestions */}
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              {suggestedAmounts.slice(0, 4).map((amount: number) => (
                <button
                  key={amount}
                  onClick={() => setSelectedBidAmount(amount)}
                  className={cn(
                    "flex-1 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors border",
                    selectedBidAmount === amount
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-secondary text-secondary-foreground border-border hover:bg-muted"
                  )}
                >
                  {formatPKR(amount)}
                </button>
              ))}
            </div>

            {/* Slider / Action Button */}
            <BidSlider
              amount={selectedBidAmount}
              onConfirm={handleBid}
              disabled={isPlacing || !isAuthenticated}
            />

            {!isAuthenticated && (
              <div className="text-center text-xs text-muted-foreground">
                <button onClick={() => navigate('/profile')} className="underline font-bold text-primary">Log in</button> to bid
              </div>
            )}
          </div>
        ) : (
          <div className="bg-muted/50 rounded-xl p-4 text-center">
            <p className="font-semibold text-foreground">Auction has ended</p>
            <p className="text-sm text-muted-foreground">Final Price: {formatPKR(currentBid)}</p>
          </div>
        )}
      </div>

      {/* Bid History Modal */}
      <BidHistoryModal
        isOpen={isBidHistoryOpen}
        onClose={() => setIsBidHistoryOpen(false)}
        bids={bids}
        auctionTitle={auction.title}
      />
    </div>
  );
}
